import React, { useState } from 'react';
import { InferProps } from 'prop-types';
import Link from 'next/link';
import { useTranslate } from 'nucleus-text';
import { injectTestId } from '@cvent/nucleus-test-automation';
import useTheme from '@cvent/blocks/hooks/useTheme';
import { Avatar } from '@components/shared/Avatar';
import useBreakpoints from '@hooks/useBreakpoints';
import useAppFeatures from '@hooks/useAppFeatures';
import useMeQuery from '@hooks/useMeQuery';
import { Badge } from '@cvent/carina/components/Badge';
import NavItemIndicator from '@components/layout/NavItemIndicator';
import { CSSObject } from '@emotion/react';
import { MoodThemeProvider } from '@components/blocks/BlockThemeProvider';
import injectCustomDatadogActionName from '@tools/injectCustomDatadogActionName';
import NavigationFlyoutControl from '@components/header/flyouts/NavigationFlyoutControl';
import Icon from '@components/shared/Icon';
import ProfileLogout from './ProfileLogout';

interface TabOption {
  label: React.ReactNode;
  rawLabel?: React.ReactNode;
  value: string;
  testID: string;
  page: string;
  fullUrl?: string;
}
export interface NavigationMenuProps extends InferProps<typeof ProfileLogout.propTypes> {
  selectedTab?: string;
  eventId: string;
  tabOptions?: TabOption[];
  unreadReportCount?: number;
  pendingConnectionsCount?: number;
  isAttendeeProfileEnabled: boolean;
  // Feature flags
  viewConnections?: boolean;
  viewYourEvents?: boolean;
}

export const menuTriggerSize = 52;

const useStyles = (isFlyoutVisible = false, hasPendingConnections = false): Record<string, CSSObject> => {
  const { backgroundColor, borderColor, size, font, depth, block } = useTheme();
  const theme = useTheme();
  const { xOffset, yOffset, blurRadius, spreadRadius, color } = depth.boxShadow.hard;
  const { isMobile } = useBreakpoints();

  return {
    wrapper: {
      display: 'flex',
      alignItems: 'center'
    },
    icon: {
      marginRight: !isMobile ? 0 : 12,
      paddingTop: !isMobile ? 1 : 0,
      marginTop: isMobile ? 4 : 0,
      position: 'relative'
    },
    iconBadge: {
      position: 'absolute',
      left: '20px',
      top: '-6px',
      border: hasPendingConnections ? `2px solid ${block.base}` : 'none',
      borderRadius: size.border.radius.round
    },
    badge: {
      position: 'absolute',
      left: '37px',
      top: '7px',
      border: hasPendingConnections ? `2px solid ${block.base}` : 'none',
      borderRadius: size.border.radius.round
    },
    menuContainer: {
      display: 'flex',
      flexDirection: 'column',
      paddingTop: 8,
      paddingBottom: 8,
      width: 300,
      borderRadius: size.border.radius.fiveX,
      boxShadow: `${xOffset} ${yOffset} ${blurRadius} ${spreadRadius} ${color}`
    },
    mobileMenuContainer: {
      backgroundColor: backgroundColor.base,
      zIndex: 100
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      textDecoration: 'none',
      color: 'inherit',
      fontSize: font.base.size.m,
      backgroundColor: backgroundColor.interactive.base,
      padding: 16,
      cursor: 'pointer',
      '&:active': {
        backgroundColor: backgroundColor.neutral.focus
      },
      '&:hover': {
        backgroundColor: theme.block.accent
      },
      '&:focus': {
        backgroundColor: backgroundColor.neutral.focus,
        outline: 'none'
      }
    },
    menuItemHighlight: {
      backgroundColor: backgroundColor.neutral.focus
    },
    separator: {
      borderBottom: 'solid',
      borderBottomWidth: size.border.base,
      borderBottomColor: borderColor.soft,
      marginLeft: 16,
      marginRight: 16
    },
    avatarButton: {
      background: 'transparent',
      border: 0,
      borderRadius: 10,
      padding: '10px 12px 10px 12px',
      cursor: 'pointer',
      '&:focus': {
        backgroundColor: isFlyoutVisible ? backgroundColor.brand.focus : null,
        // imitating Chrome default focus outline in Firefox
        '@-moz-document url-prefix()': {
          outline: '2px solid blue',
          boxShadow: '0 0 0 3px white'
        }
      },
      '&:hover': { backgroundColor: backgroundColor.brand.hover }
    },
    activeBadge: {
      borderColor: backgroundColor.brand.hover
    }
  };
};

interface MenuItemProps {
  highlight?: boolean;
  testID: string;
  href: string;
  onClick?: () => void;
  target?: string;
  rel?: string;
  badgeText?: string;
}

function MenuItem({
  children,
  highlight,
  testID,
  href,
  onClick,
  target,
  rel,
  badgeText
}: React.PropsWithChildren<MenuItemProps>): JSX.Element {
  const styles = useStyles();
  return (
    <Link
      href={href}
      passHref
      css={[styles.menuItem, highlight ? styles.menuItemHighlight : null]}
      tabIndex={0}
      onClick={onClick}
      target={target}
      rel={rel}
      {...injectTestId(testID)}
    >
      {children}
      {badgeText && <NavItemIndicator text={badgeText} testID={`${testID}-badge`} />}
    </Link>
  );
}

function MenuSeparator() {
  const styles = useStyles();
  return <div {...injectTestId('profile-menu-separator')} css={styles.separator} />;
}

function MenuContent({
  tabOptions,
  selectedTab,
  isAttendeeProfileEnabled,
  eventId,
  setIsOpen,
  viewConnections,
  unreadReportCount,
  pendingConnectionsCount,
  viewYourEvents,
  attendeeName,
  avatarName,
  attendeeEmail,
  attendeeProfilePhotoSrc,
  onLogout
}) {
  const styles = useStyles();
  const { translate } = useTranslate();
  const { communitySafety } = useAppFeatures();
  const { data } = useMeQuery();

  return (
    <div
      css={styles.menuContainer}
      {...injectTestId('profile-menu')}
      {...injectCustomDatadogActionName('Profile navigation menu')}
    >
      {tabOptions.map(({ value, rawLabel, testID }) => (
        // Solve (validateDOMNesting(...): <a> cannot appear as a descendant of <a>) in mobile mode
        <MenuItem key={value} testID={testID} highlight={selectedTab === value} href={value}>
          {rawLabel}
        </MenuItem>
      ))}
      {tabOptions.length > 0 ? <MenuSeparator /> : null}
      {isAttendeeProfileEnabled && (
        <>
          <MenuItem
            href={`/events/${eventId}/profile-edit`}
            onClick={() => setIsOpen(false)}
            testID="navigation-menu-profile-edit"
          >
            {translate('Navigation-Menu-Profile')}
          </MenuItem>
          <MenuSeparator />

          <MenuItem
            href={`/events/${eventId}/settings`}
            onClick={() => setIsOpen(false)}
            testID="navigation-menu-settings"
          >
            {translate('Navigation-Menu-Settings')}
          </MenuItem>
          <MenuSeparator />
        </>
      )}
      {viewConnections ? (
        <>
          <MenuItem
            href={`/events/${eventId}/connections`}
            onClick={() => setIsOpen(false)}
            testID="navigation-menu-connections"
            badgeText={pendingConnectionsCount ? String(pendingConnectionsCount) : undefined}
          >
            {translate('Navigation-Menu-Connections')}
          </MenuItem>
          <MenuSeparator />
        </>
      ) : null}
      {viewYourEvents ? (
        <>
          <MenuItem
            href={`/events/${eventId}/your-events`}
            onClick={() => setIsOpen(false)}
            testID="navigation-menu-your-events"
          >
            {translate('Navigation-Menu-YourEvents')}
          </MenuItem>
          <MenuSeparator />
        </>
      ) : null}
      {communitySafety && data?.me?.permissions?.isGlobalModerator ? (
        <>
          <MenuItem
            href={`/events/${eventId}/incidents`}
            onClick={() => setIsOpen(false)}
            badgeText={unreadReportCount ? String(unreadReportCount) : undefined}
            testID="navigation-menu-reported-incidents"
          >
            {translate('CommunitySafety-IncidentsScreen-Title')}
          </MenuItem>
          <MenuSeparator />
        </>
      ) : null}
      <ProfileLogout
        attendeeName={attendeeName}
        avatarName={avatarName}
        attendeeEmail={attendeeEmail}
        attendeeProfilePhotoSrc={attendeeProfilePhotoSrc}
        onLogout={onLogout}
      />
    </div>
  );
}

function NavigationMenu({
  tabOptions = [],
  attendeeName,
  avatarName,
  attendeeEmail,
  attendeeProfilePhotoSrc,
  isAttendeeProfileEnabled,
  onLogout,
  selectedTab,
  eventId,
  viewYourEvents,
  viewConnections,
  unreadReportCount,
  pendingConnectionsCount
}: NavigationMenuProps) {
  const { block, font, size } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const hasPendingConnections = !!pendingConnectionsCount;
  const hasUnreadReports = !!unreadReportCount;

  const notificationCount = pendingConnectionsCount + unreadReportCount;

  const styles = useStyles(isOpen, hasPendingConnections);

  const badge =
    hasPendingConnections || hasUnreadReports ? (
      <Badge
        content={notificationCount.toString()}
        {...injectTestId('navigation-menu-profile-badge')}
        variant="danger"
      />
    ) : null;

  return (
    <NavigationFlyoutControl
      accessibilityLabel={attendeeName}
      icon={
        attendeeProfilePhotoSrc ? (
          <>
            <Avatar
              size={2}
              image={attendeeProfilePhotoSrc}
              text={avatarName}
              backgroundColorOverride={block.accent}
              testID="page-header-menu-avatar"
            />
            <div
              css={[styles.badge, isOpen ? { ...styles.activeBadge } : null]}
              {...injectTestId('navigation-menu-badge-container')}
            >
              {badge}
            </div>
          </>
        ) : (
          <div css={styles.wrapper} {...injectTestId('attendee-messaging-message-icon')}>
            <div css={styles.icon}>
              <Icon name="person-circle" size="md" color={font.color.base} aria-hidden />
              <div css={[styles.iconBadge, isOpen ? { ...styles.activeBadge } : null]}>{badge}</div>
            </div>
          </div>
        )
      }
      testID="page-header-menu"
      isFlyoutVisible={isOpen}
      onFlyoutVisible={setIsOpen}
    >
      <MoodThemeProvider css={{ borderRadius: size.border.radius.fiveX }}>
        <MenuContent
          tabOptions={tabOptions}
          selectedTab={selectedTab}
          isAttendeeProfileEnabled={isAttendeeProfileEnabled}
          eventId={eventId}
          setIsOpen={setIsOpen}
          viewConnections={viewConnections}
          unreadReportCount={unreadReportCount}
          pendingConnectionsCount={pendingConnectionsCount}
          viewYourEvents={viewYourEvents}
          attendeeName={attendeeName}
          avatarName={avatarName}
          attendeeEmail={attendeeEmail}
          attendeeProfilePhotoSrc={attendeeProfilePhotoSrc}
          onLogout={onLogout}
        />
      </MoodThemeProvider>
    </NavigationFlyoutControl>
  );
}

export default NavigationMenu;
